import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Quote, QuoteLineItem, QuoteTemplate, PlaceholderPosition, PaymentCondition } from '@/types/quote';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, X, Eye, AlertTriangle, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuotePDF } from '@/hooks/useQuotePDF';
import { Document, Page, pdfjs } from 'react-pdf';
import { supabase } from '@/integrations/supabase/client';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface QuotePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Quote;
  lineItems: QuoteLineItem[];
}

interface CompanySettings {
  name?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  kvk?: string;
  btw?: string;
  iban?: string;
  bic?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('nl-NL', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const QuotePreviewModal = ({
  open,
  onOpenChange,
  quote,
  lineItems
}: QuotePreviewModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(0.8);
  const [template, setTemplate] = useState<QuoteTemplate | null>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const { generatePDF, isGenerating } = useQuotePDF();

  // Fetch template and company settings
  useEffect(() => {
    const fetchData = async () => {
      if (!open) return;
      setLoading(true);

      try {
        // Fetch template
        if (quote.template_id) {
          const { data: templateData } = await supabase
            .from('quote_templates')
            .select('*')
            .eq('id', quote.template_id)
            .single();

          if (templateData) {
            // Parse JSON fields
            const parsedTemplate: QuoteTemplate = {
              ...templateData,
              placeholder_positions: typeof templateData.placeholder_positions === 'string'
                ? JSON.parse(templateData.placeholder_positions)
                : templateData.placeholder_positions,
              page_backgrounds: typeof templateData.page_backgrounds === 'string'
                ? JSON.parse(templateData.page_backgrounds)
                : templateData.page_backgrounds,
              default_payment_conditions: typeof templateData.default_payment_conditions === 'string'
                ? JSON.parse(templateData.default_payment_conditions)
                : templateData.default_payment_conditions,
            };
            setTemplate(parsedTemplate);
          }
        }

        // Fetch company settings
        const { data: settingsData } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'company_settings')
          .maybeSingle();

        if (settingsData?.value) {
          setCompanySettings(settingsData.value as CompanySettings);
        }
      } catch (error) {
        console.error('Error fetching preview data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, quote.template_id]);

  // Calculate totals from line items
  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const vat = lineItems.reduce((sum, item) => {
      const itemVat = (item.total || 0) * ((item.vat_rate || 21) / 100);
      return sum + itemVat;
    }, 0);
    return { subtotal, vat, total: subtotal + vat };
  }, [lineItems]);

  // Get total pages
  const totalPages = template?.page_count || template?.pdf_page_count || 1;

  // Replace placeholders with actual values
  const replacePlaceholder = (key: string): string => {
    const company = companySettings || {};
    const org = quote.organization;
    const contact = quote.contact;
    
    switch (key) {
      // Company
      case '{{company_name}}': return company.name || '';
      case '{{company_street}}': return `${company.street || ''} ${company.house_number || ''}`.trim();
      case '{{company_postal_code}}': return company.postal_code || '';
      case '{{company_city}}': return company.city || '';
      case '{{company_kvk}}': return company.kvk || '';
      case '{{company_btw}}': return company.btw || '';
      case '{{company_iban}}': return company.iban || '';
      case '{{company_phone}}': return company.phone || '';
      case '{{company_email}}': return company.email || '';
      case '{{company_website}}': return company.website || '';
      
      // Organization
      case '{{organization_name}}': return org?.name || '';
      
      // Contact
      case '{{contact_name}}': return contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : '';
      case '{{contact_email}}': return contact?.email || '';
      
      // Quote
      case '{{quote_number}}': return quote.quote_number || '';
      case '{{quote_title}}': return quote.title || '';
      case '{{quote_date}}': return formatDate(quote.created_at);
      case '{{valid_until}}': return formatDate(quote.valid_until);
      
      // Financial
      case '{{subtotal}}': return formatCurrency(totals.subtotal);
      case '{{vat_amount}}': return formatCurrency(totals.vat);
      case '{{total}}': return formatCurrency(totals.total);
      case '{{vat_rate}}': return '21%';
      
      // Sections
      case '{{project_goal}}': return quote.project_goal || '';
      case '{{work_description}}': return quote.work_description || '';
      case '{{advice_conclusion}}': return quote.advice_conclusion || '';
      
      // Misc
      case '{{current_date}}': return formatDate(new Date().toISOString());
      
      default: return key;
    }
  };

  // Check for missing placeholders
  const getMissingPlaceholders = (): string[] => {
    const missing: string[] = [];
    const positions = template?.placeholder_positions || [];
    
    for (const pos of positions) {
      if (pos.key === '{{company_logo}}' || pos.key === '{{line_items_table}}' || pos.key === '{{payment_conditions}}' || pos.key === '{{signature_block}}') {
        continue; // Skip special placeholders
      }
      const value = replacePlaceholder(pos.key);
      if (!value || value === pos.key) {
        missing.push(pos.key);
      }
    }
    
    return missing;
  };

  const missingPlaceholders = useMemo(() => getMissingPlaceholders(), [template, quote, companySettings]);

  // Render a single page preview
  const renderPagePreview = (pageNumber: number) => {
    const pagePositions = (template?.placeholder_positions || []).filter(p => p.page === pageNumber);
    const pageBackground = template?.page_backgrounds?.find(bg => bg.page === pageNumber);
    
    return (
      <div
        className="relative bg-white shadow-lg"
        style={{
          width: 595 * scale,
          height: 842 * scale,
        }}
      >
        {/* Background */}
        {template?.pdf_template_url && (
          <div className="absolute inset-0">
            <Document
              file={template.pdf_template_url}
              loading={<div className="w-full h-full bg-gray-100 animate-pulse" />}
            >
              <Page
                pageNumber={pageNumber}
                width={595 * scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        )}
        
        {/* Blank page indicator */}
        {(!template?.pdf_template_url || pageBackground?.type === 'blank') && (
          <div className="absolute inset-0 border-2 border-dashed border-gray-200 flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Pagina {pageNumber}</p>
          </div>
        )}
        
        {/* Placeholders */}
        {pagePositions.map((pos, index) => {
          // Special rendering for certain placeholders
          if (pos.key === '{{company_logo}}') {
            return (
              <div
                key={index}
                className="absolute overflow-hidden"
                style={{
                  left: pos.x * scale,
                  top: pos.y * scale,
                  width: pos.width * scale,
                  height: pos.height * scale,
                }}
              >
                {companySettings?.logo_url ? (
                  <img 
                    src={companySettings.logo_url} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-muted-foreground">
                    Logo
                  </div>
                )}
              </div>
            );
          }
          
          if (pos.key === '{{line_items_table}}') {
            return (
              <div
                key={index}
                className="absolute overflow-hidden bg-white/80"
                style={{
                  left: pos.x * scale,
                  top: pos.y * scale,
                  width: pos.width * scale,
                  fontSize: 8 * scale,
                }}
              >
                <table className="w-full border-collapse" style={{ fontSize: 8 * scale }}>
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-1 border-b">Omschrijving</th>
                      <th className="text-right p-1 border-b">Aantal</th>
                      <th className="text-right p-1 border-b">Prijs</th>
                      <th className="text-right p-1 border-b">Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.slice(0, 5).map((item, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="p-1 truncate max-w-[150px]">{item.description}</td>
                        <td className="text-right p-1">{item.quantity}</td>
                        <td className="text-right p-1">{formatCurrency(item.unit_price)}</td>
                        <td className="text-right p-1">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                    {lineItems.length > 5 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted-foreground p-1">
                          +{lineItems.length - 5} meer items...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          }
          
          if (pos.key === '{{payment_conditions}}') {
            const conditions = quote.payment_conditions as PaymentCondition[] || [];
            return (
              <div
                key={index}
                className="absolute overflow-hidden"
                style={{
                  left: pos.x * scale,
                  top: pos.y * scale,
                  width: pos.width * scale,
                  fontSize: 8 * scale,
                }}
              >
                <p className="font-semibold mb-1">Betalingscondities:</p>
                {conditions.map((c, i) => (
                  <p key={i} className="text-xs">• {c.percentage}% - {c.description}</p>
                ))}
              </div>
            );
          }
          
          if (pos.key === '{{signature_block}}') {
            return (
              <div
                key={index}
                className="absolute border-t-2 border-gray-400 pt-2"
                style={{
                  left: pos.x * scale,
                  top: pos.y * scale,
                  width: pos.width * scale,
                  fontSize: 9 * scale,
                }}
              >
                <p className="font-semibold">Voor akkoord:</p>
                <div className="mt-4 pt-8 border-b border-gray-400 w-48" style={{ width: 150 * scale }}>
                  <p className="text-xs text-muted-foreground">Handtekening</p>
                </div>
                <p className="mt-2">{quote.contact?.first_name} {quote.contact?.last_name}</p>
                <p className="text-xs text-muted-foreground">{quote.organization?.name}</p>
                {quote.additional_conditions && (
                  <div className="mt-6 pt-4">
                    <div className="flex items-center gap-1 mb-2">
                      <div className="w-1 h-4 rounded-sm" style={{ backgroundColor: '#2D5A27' }} />
                      <p className="font-semibold text-sm">Voorwaarden</p>
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{quote.additional_conditions}</p>
                  </div>
                )}
              </div>
            );
          }
          
          // Regular text placeholder
          const value = replacePlaceholder(pos.key);
          const isMissing = !value || value === pos.key;
          
          return (
            <div
              key={index}
              className={cn(
                "absolute overflow-hidden whitespace-pre-wrap",
                isMissing && "bg-amber-100 border border-amber-300 rounded"
              )}
              style={{
                left: pos.x * scale,
                top: pos.y * scale,
                width: pos.width * scale,
                fontSize: (pos.fontSize || 10) * scale,
                fontWeight: pos.fontWeight || 'normal',
                color: pos.color || '#000000',
                textAlign: pos.align as any || 'left',
              }}
            >
              {isMissing ? (
                <span className="text-amber-600 text-xs">{pos.key}</span>
              ) : (
                value
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleDownload = async () => {
    await generatePDF(quote.id, true, true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-primary" />
              <DialogTitle>Live Preview - {quote.title}</DialogTitle>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Page Navigation */}
              <div className="flex items-center gap-1 bg-muted rounded-lg px-2 py-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium min-w-[60px] text-center">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Zoom */}
              <div className="flex items-center gap-1 bg-muted rounded-lg px-2 py-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium min-w-[50px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setScale(s => Math.min(1.5, s + 0.1))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Download */}
              <Button
                onClick={handleDownload}
                disabled={isGenerating}
              >
                <Download className="h-4 w-4 mr-2" />
                {isGenerating ? 'Genereren...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex flex-1 min-h-0">
          {/* Missing placeholders warning sidebar */}
          {missingPlaceholders.length > 0 && (
            <div className="w-64 border-r bg-amber-50 p-4 shrink-0">
              <div className="flex items-center gap-2 text-amber-700 mb-3">
                <AlertTriangle className="h-4 w-4" />
                <h4 className="font-semibold text-sm">Ontbrekende data</h4>
              </div>
              <p className="text-xs text-amber-600 mb-3">
                De volgende placeholders hebben geen waarde:
              </p>
              <ul className="space-y-1">
                {missingPlaceholders.map((key, i) => (
                  <li key={i} className="text-xs font-mono bg-amber-100 px-2 py-1 rounded">
                    {key}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Preview area */}
          <ScrollArea className="flex-1">
            <div className="p-8 min-h-full flex justify-center">
              {loading ? (
                <div className="w-[595px] h-[842px] bg-muted animate-pulse rounded-lg" style={{ transform: `scale(${scale})` }} />
              ) : !template ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Geen template gekoppeld aan deze offerte
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    De PDF wordt gegenereerd met het standaard formaat
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {renderPagePreview(currentPage)}
                </div>
              )}
            </div>
          </ScrollArea>
          
          {/* Page thumbnails */}
          <div className="w-32 border-l bg-muted/50 p-2 shrink-0">
            <p className="text-xs font-semibold text-muted-foreground mb-2 px-2">Pagina's</p>
            <div className="space-y-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={cn(
                    "w-full aspect-[1/1.414] bg-white border rounded shadow-sm flex items-center justify-center text-xs transition-all",
                    currentPage === i + 1 
                      ? "border-primary ring-2 ring-primary/30" 
                      : "hover:border-primary/50"
                  )}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
